import { useSyncExternalStore } from 'react'

/**
 * Per-viewer view choices for the Signals queue: the flat-table columns, the
 * fields on storyline (subgraph) member rows, row density and the Flat page
 * size. localStorage, ONE key for the viewer (not per scope — it is how they
 * like to read, not data):
 * `bloo.signals.view`. A draft contract, documented in docs/signals.md. Same
 * house rules as the other local stores: try-catch, SSR-safe, tolerant read —
 * unknown ids are dropped, a corrupt value falls back to the defaults.
 */

export const VIEW_STORAGE_KEY = 'bloo.signals.view'

/**
 * Flat-table columns. `detection` (the name) and the row actions are fixed
 * (always shown), so they are not here. Severity is the row's edge colour,
 * never a column.
 */
export const TABLE_COLUMNS = [
  'time',
  'tags',
  'entities',
  'tactic',
  'streams',
  'occurrences',
  'stage',
  'confidence',
  'first',
  'uuid',
] as const
export type TableColumn = (typeof TABLE_COLUMNS)[number]

export const TABLE_COLUMN_LABEL: Record<TableColumn, string> = {
  time: 'last seen',
  tags: 'signal tags',
  entities: 'entities',
  tactic: 'tactic',
  streams: 'streams',
  occurrences: 'occurrences',
  stage: 'stage',
  confidence: 'confidence',
  first: 'first seen',
  uuid: 'uuid',
}

export const DEFAULT_TABLE_COLUMNS: readonly TableColumn[] = ['time', 'tags', 'entities']

/** Fields on a storyline (subgraph) member row (beside the always-shown severity + detection name). */
export const MEMBER_FIELDS = ['time', 'tags', 'entities'] as const
export type MemberField = (typeof MEMBER_FIELDS)[number]

export const MEMBER_FIELD_LABEL: Record<MemberField, string> = {
  time: 'time',
  tags: 'signal tags',
  entities: 'entities',
}

export const DEFAULT_MEMBER_FIELDS: readonly MemberField[] = ['time', 'tags', 'entities']

/** Flat page sizes; the server pages (signals/list pageno/pagesize). */
export const PAGE_SIZES = [25, 50, 100] as const
export type PageSize = (typeof PAGE_SIZES)[number]
export const DEFAULT_PAGE_SIZE: PageSize = 50

export type Density = 'comfortable' | 'compact'

export interface SignalView {
  columns: TableColumn[]
  memberFields: MemberField[]
  density: Density
  pageSize: PageSize
}

/**
 * Schema 2: severity / AI hint / status / score columns gone, page size
 * added. A schema-1 value keeps its density only — its column choice was
 * made against the old set.
 */
export const VIEW_SCHEMA = 2

export const DEFAULT_VIEW: SignalView = {
  columns: [...DEFAULT_TABLE_COLUMNS],
  memberFields: [...DEFAULT_MEMBER_FIELDS],
  density: 'comfortable',
  pageSize: DEFAULT_PAGE_SIZE,
}

function pickKnown<T extends string>(v: unknown, known: readonly T[], fallback: readonly T[]): T[] {
  if (!Array.isArray(v)) return [...fallback]
  const set = new Set(v.filter((x): x is T => typeof x === 'string' && (known as readonly string[]).includes(x)))
  // Keep the canonical order so the table never reshuffles on a toggle.
  return known.filter((k) => set.has(k))
}

export function readView(v: unknown): SignalView {
  if (typeof v !== 'object' || v === null) return DEFAULT_VIEW
  const o = v as Record<string, unknown>
  const current = o.schema === VIEW_SCHEMA
  return {
    columns: current ? pickKnown(o.columns, TABLE_COLUMNS, DEFAULT_TABLE_COLUMNS) : [...DEFAULT_TABLE_COLUMNS],
    memberFields: current ? pickKnown(o.memberFields, MEMBER_FIELDS, DEFAULT_MEMBER_FIELDS) : [...DEFAULT_MEMBER_FIELDS],
    density: o.density === 'compact' ? 'compact' : 'comfortable',
    pageSize: (PAGE_SIZES as readonly unknown[]).includes(o.pageSize) ? (o.pageSize as PageSize) : DEFAULT_PAGE_SIZE,
  }
}

export interface SignalViewStore {
  get: () => SignalView
  set: (patch: Partial<SignalView>) => void
  subscribe: (cb: () => void) => () => void
}

let store: SignalViewStore | null = null

export function signalViewStore(): SignalViewStore {
  if (store) return store
  let cache: SignalView | null = null
  const listeners = new Set<() => void>()
  const read = (): SignalView => {
    if (cache) return cache
    try {
      const raw = globalThis.localStorage?.getItem(VIEW_STORAGE_KEY)
      cache = readView(raw ? JSON.parse(raw) : null)
    } catch {
      cache = DEFAULT_VIEW
    }
    return cache
  }
  const write = (next: SignalView) => {
    cache = next
    try {
      globalThis.localStorage?.setItem(VIEW_STORAGE_KEY, JSON.stringify({ schema: VIEW_SCHEMA, ...next }))
    } catch {
      // A view choice is a convenience; a refused write keeps the in-memory value.
    }
    for (const cb of listeners) cb()
  }
  store = {
    get: read,
    set: (patch) => write(readView({ schema: VIEW_SCHEMA, ...read(), ...patch })),
    subscribe: (cb) => {
      listeners.add(cb)
      return () => {
        listeners.delete(cb)
      }
    },
  }
  return store
}

/** Test hook: forget the cached store (a test swaps localStorage between cases). */
export function resetSignalViewStore() {
  store = null
}

export function useSignalView(): { view: SignalView; store: SignalViewStore } {
  const s = signalViewStore()
  const view = useSyncExternalStore(s.subscribe, s.get, s.get)
  return { view, store: s }
}
